import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useMeetingNotesTableQuery } from '@/features/meetings/hooks/useMeetingNotesTableQuery'
import { MeetingNoteCard } from './MeetingNoteCard'

const INITIAL_SKELETON_KEYS = ['s1', 's2', 's3']

export function MeetingNotesList() {
  const queryResult = useMeetingNotesTableQuery({
    limit: 25,
    sort_by: 'event_time',
    sort_order: 'desc',
  })

  const notes = React.useMemo(
    () => queryResult.data?.pages.flatMap((p) => p.data) ?? [],
    [queryResult.data]
  )
  const hasMore = queryResult.hasNextPage
  const isLoading = queryResult.isLoading
  const isFetchingNextPage = queryResult.isFetchingNextPage
  const fetchNextPage = queryResult.fetchNextPage

  const sentinelRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return
      const observer = new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting) fetchNextPage()
      })
      observer.observe(node)
      return () => observer.disconnect()
    },
    [fetchNextPage]
  )

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 overflow-y-auto p-4 h-full min-h-0">
        {INITIAL_SKELETON_KEYS.map((key) => (
          <NoteCardSkeleton key={key} />
        ))}
      </div>
    )
  }

  if (queryResult.isError) {
    return <ErrorState onRetry={() => queryResult.refetch()} />
  }

  if (notes.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-12">
        No meeting notes yet.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 overflow-y-auto p-4 h-full min-h-0">
      {notes.map((note) => (
        <MeetingNoteCard key={note.id} note={note} />
      ))}
      {isFetchingNextPage && <NoteCardSkeleton key="loading" />}
      {hasMore && !isFetchingNextPage && (
        <div ref={sentinelRef} aria-hidden="true" className="h-px" />
      )}
    </div>
  )
}

function NoteCardSkeleton() {
  return (
    <Card className="flex-row items-center justify-between gap-4 px-5 py-5">
      <div className="flex items-start gap-4 min-w-0 flex-1">
        <Skeleton className="size-[22px] shrink-0 mt-0.5 rounded-md" />
        <div className="flex flex-col gap-2 min-w-0 flex-1">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
    </Card>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 text-center py-12">
      <p className="text-muted-foreground">
        Couldn’t load meeting notes. Please try again.
      </p>
      <Button size="sm" variant="outline" onClick={onRetry}>
        Retry
      </Button>
    </div>
  )
}
